import AddLocationIcon from '@mui/icons-material/AddLocation';
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import { Card, CardHeader } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import NewFieldMap from '/@/components/map/new-field-map';
import useUnitProvider from '/@/core/context/unit';
import { buildOnlineField } from '/@/core/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';
import MuiFab from '/@/mui/fab';
import { getSelectedFeature } from '/@/store/nav/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { convertCountryBoundsToGoogleLatLng } from '../../field-new-v2/utils';
import NameInput from './name-input';
import styles from './styles.module.css';

const getEvent = (prefix, mode) => ({ key: `${prefix}${mode === 'point' ? 'Point' : 'Polygon'}` });

const OnlineFieldEditor = ({ zones, sessionCountry, sessionCountries, onError, onCreateField }) => {
    const dispatch = useDispatch();
    const country = useSelector(getSessionCountry);
    const units = useUnitProvider();
    const selectedFeature = useSelector(getSelectedFeature);
    const actions = useMemo(() => bindActionCreators({ trackEvent }, dispatch));
    const [field, setField] = useState({ name: '', geometry: [] });
    const [loading, setLoading] = useState(false);
    const [showName, setShowName] = useState(false);
    const [editMode, setEditMode] = useState(undefined);
    const mapRef = useRef(null);
    useEffect(() => {
        if (mapRef.current && country) {
            const googleBounds = convertCountryBoundsToGoogleLatLng(country.bounds);
            mapRef.current.fitBounds(googleBounds);
        }
    }, [mapRef]);

    const validatePoints = useMemo(() => {
        if (!field) return false;
        const numPoints = field.geometry.length;
        if (editMode === 'point') {
            return numPoints === 1;
        }
        if (editMode === 'polygon') {
            return numPoints >= 3;
        }
        if (editMode === 'circle') {
            return true;
        }
        return false;
    }, [field, editMode]);

    const handleSetEditMode = (mode) => () => {
        setEditMode(mode);
        actions.trackEvent(getEvent('fields.draw', mode));
    };

    const handleEditModeReset = () => {
        actions.trackEvent(getEvent('fields.reset', editMode));
        setEditMode(undefined);
        setField((prev) => ({ ...prev, geometry: [] }));
    };

    const handleShowName = () => {
        actions.trackEvent(getEvent('fields.accept', editMode));
        setShowName(true);
    };

    const handleCancelName = () => {
        actions.trackEvent({ key: 'fields.cancelNaming' });
        setShowName(false);
    };
    const handleSubmit = async (name) => {
        actions.trackEvent(getEvent('fields.save', editMode));
        setLoading(true);
        const { field: newField, error } = await buildOnlineField({
            payload: { ...field, name },
            zones,
            sessionCountry,
            sessionCountries,
        });
        setField(newField);
        if (!error) return onCreateField({ ...newField, area: polygonArea, editMode });
        onError({ error, field: newField });
        setEditMode(undefined);
        setLoading(false);
        setShowName(false);
    };

    const polygonArea = useMemo(() => {
        return google.maps.geometry.spherical.computeArea(
            (field?.geometry || []).map(([lng, lat]) => new google.maps.LatLng(lat, lng))
        );
    }, [field]);

    const areaDisplay = useMemo(() => convertAreaFromM2(polygonArea, units).toString(), [polygonArea, units]);

    return (
        <div className={styles.root}>
            <NewFieldMap
                ref={mapRef}
                disabled={showName}
                mode={editMode}
                field={field || {}}
                setField={setField}
                country={country.code}
            />
            {showName && <NameInput loading={loading} onSubmit={handleSubmit} onCancel={handleCancelName} />}
            {!!polygonArea && (
                <Card
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: (t) => `translate(-50%, -${t.spacing(7.5)})`,
                        minWidth: (t) => t.spacing(12),
                    }}
                >
                    <CardHeader title={areaDisplay} titleTypographyProps={{ align: 'center' }} />
                </Card>
            )}
            {!editMode && (
                <div className={styles.bottomControls}>
                    {(!selectedFeature ||
                        Array.isArray(selectedFeature) ||
                        typeof selectedFeature === 'object' ||
                        !selectedFeature.selectedFeature ||
                        !selectedFeature.selectedFeature.toLowerCase().includes('nitrogen')) && (
                        <div className={styles.flexHorizontalAlignRight}>
                            <MuiFab
                                Icon={<AddLocationIcon />}
                                TooltipProps={{
                                    title: _('Marcar un punto'),
                                    placement: 'left',
                                }}
                                FabProps={{
                                    'aria-label': 'add-point',
                                    onClick: handleSetEditMode('point'),
                                    size: 'medium',
                                    sx: {
                                        '&:hover': {
                                            transform: 'scale(1.1)',
                                        },
                                    },
                                }}
                            />
                        </div>
                    )}
                    <div className={styles.flexHorizontalAlignRight}>
                        <MuiFab
                            Icon={<BorderOuterIcon />}
                            TooltipProps={{
                                title: _('Dibujar un polígono'),
                                placement: 'left',
                            }}
                            FabProps={{
                                'aria-label': 'add-point',
                                onClick: handleSetEditMode('polygon'),
                                size: 'medium',
                                sx: {
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                    },
                                },
                            }}
                        />
                    </div>
                    <div className={styles.flexHorizontalAlignRight}>
                        <MuiFab
                            Icon={<TripOriginIcon />}
                            TooltipProps={{
                                title: _('Dibujar un círculo'),
                                placement: 'left',
                            }}
                            FabProps={{
                                'aria-label': 'add-point',
                                onClick: handleSetEditMode('circle'),
                                size: 'medium',
                                sx: {
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                    },
                                },
                            }}
                        />
                    </div>
                </div>
            )}
            {editMode && (
                <div className={styles.bottomControls}>
                    <MuiFab
                        Icon={<ReplayIcon />}
                        TooltipProps={{
                            title: _('Regresar'),
                            placement: 'left',
                        }}
                        FabProps={{
                            'aria-label': 'add-point',
                            onClick: () => handleEditModeReset(),
                            size: 'medium',
                            sx: {
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                            },
                        }}
                    />
                    <div className={styles.flexHorizontalSpaceBetween}>
                        <div className={styles.helpText}>
                            {editMode === 'point'
                                ? _('Marcá un punto sobre el lote')
                                : editMode === 'circle'
                                  ? _('Ajustá el tamaño del lote')
                                  : _('Marcá los vértices del lote')}
                        </div>
                        <MuiFab
                            Icon={<CheckIcon />}
                            TooltipProps={{
                                title: _('Guardar'),
                                placement: 'left',
                            }}
                            FabProps={{
                                'aria-label': 'add-point',
                                onClick: () => handleShowName(),
                                size: 'medium',
                                sx: {
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'grey',
                                    },
                                },
                                disabled: !validatePoints,
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

OnlineFieldEditor.propTypes = {
    zones: PropTypes.object.isRequired,
    sessionCountry: PropTypes.object.isRequired,
    sessionCountries: PropTypes.array.isRequired,
    onError: PropTypes.func.isRequired,
    onCreateField: PropTypes.func.isRequired,
};

export default OnlineFieldEditor;
